/**
 * Shared authenticated ActionCable consumer.
 * Appends the JWT token as a query parameter so the Rails
 * ApplicationCable::Connection can identify the current user.
 */

import { createConsumer } from '@rails/actioncable';
import type { Consumer } from '@rails/actioncable';
import { config } from '@/config/env';
import { getAuthToken } from '@/services/api/client';

let consumer: Consumer | null = null;

/**
 * Returns an ActionCable consumer with the current JWT token.
 * Reuses the existing consumer if the token hasn't changed;
 * disconnects and rebuilds otherwise (e.g. after login/logout).
 */
let lastToken: string | null = null;

export function getAuthenticatedConsumer(): Consumer {
  const token = getAuthToken();

  if (consumer && token === lastToken) {
    return consumer;
  }

  // Disconnect stale consumer when token changes
  if (consumer) {
    consumer.disconnect();
  }

  const baseUrl = config.websocket.url;
  const separator = baseUrl.includes('?') ? '&' : '?';
  const url = token ? `${baseUrl}${separator}token=${encodeURIComponent(token)}` : baseUrl;

  consumer = createConsumer(url);
  lastToken = token;

  return consumer;
}
